import { useCallback, useState } from "react"
import { ConceptiveConstellationMark } from "./ConceptiveConstellationMark"
import { RepositoryConcepts } from "./RepositoryConcepts"
import { playUiClick } from "../lib/uiSound"

const MENU_SIGILS = {
  conceptive: {
    lines: [[4, 12, 10, 5], [10, 5, 18, 7], [4, 12, 11, 18], [11, 18, 19, 15]],
    nodes: [[4, 12], [10, 5], [18, 7], [11, 18], [19, 15]]
  },
  started: {
    lines: [[4, 17, 9, 8], [9, 8, 15, 12], [15, 12, 20, 4], [15, 12, 20, 19]],
    nodes: [[4, 17], [9, 8], [15, 12], [20, 4], [20, 19]]
  }
}

function MenuSigil({ type }) {
  const sigil = MENU_SIGILS[type]

  return (
    <svg className="conceptive-landing-menu-sigil" viewBox="0 0 24 24" aria-hidden="true">
      {sigil.lines.map(([x1, y1, x2, y2], index) => (
        <line key={`line-${index}`} x1={x1} y1={y1} x2={x2} y2={y2} />
      ))}
      {sigil.nodes.map(([cx, cy], index) => (
        <circle key={`node-${index}`} cx={cx} cy={cy} r={index === 0 ? 1.8 : 1.35} className={index === 0 ? "is-core" : undefined} />
      ))}
    </svg>
  )
}

export function ConceptiveLandingPage({ alert = null, developerControls = null, landingRepository = null, paths }) {
  const [concept, setConcept] = useState(() => landingRepository?.concept || {})
  const [replayRequested, setReplayRequested] = useState(false)
  const [replayActive, setReplayActive] = useState(false)
  const [graphReady, setGraphReady] = useState(false)
  const [graphMotionStarted, setGraphMotionStarted] = useState(false)
  const [commandCopied, setCommandCopied] = useState(false)
  const graphAvailable = Boolean(landingRepository?.concept)
  const playbackPath = graphAvailable ? landingRepository.paths?.repositoryPlay : null

  async function copyInitCommand() {
    playUiClick()
    await navigator.clipboard.writeText("conceptive init")
    setCommandCopied(true)
    window.setTimeout(() => setCommandCopied(false), 1800)
  }

  function selectConcept(nextConcept, event, { view = null } = {}) {
    event?.preventDefault()
    if (!nextConcept) return
    playUiClick()

    if (nextConcept.atlasType === "persona" || nextConcept.atlasType === "action") {
      setConcept((current) => ({
        ...current,
        selectedConceptKey: null,
        selectedConceptView: null,
        selectedAtlas: {
          type: nextConcept.atlasType,
          key: nextConcept.contextRouteKey || nextConcept.contextKey || nextConcept.key?.replace(`${nextConcept.atlasType}:`, "")
        }
      }))
      return
    }

    const key = nextConcept.key || null
    if (!key) return
    setConcept((current) => ({
      ...current,
      selectedAtlas: null,
      selectedConceptKey: key,
      selectedConceptView: view
    }))
  }

  function closeConcept(event) {
    event?.preventDefault()
    setConcept((current) => ({
      ...current,
      selectedAtlas: null,
      selectedConceptKey: null,
      selectedConceptView: null
    }))
  }

  function openReplay(event) {
    event?.preventDefault()
    if (!playbackPath) return
    window.history.pushState({}, "", playbackPath)
    setReplayRequested(true)
  }

  function closeReplay() {
    window.history.pushState({}, "", "/")
    setReplayRequested(false)
  }

  const handleReplaySessionChange = useCallback((closeReplaySession) => {
    setReplayActive(Boolean(closeReplaySession))
  }, [])

  // Keep navigation available while a requested replay is still loading or
  // unavailable. Once the player actually opens, clear it out of the story's
  // way; the brand remains as the way home on larger screens.
  return (
    <div className={`conceptive-landing workspace-shell is-atlas-active ${graphReady ? "is-graph-ready" : ""} ${replayActive ? "is-replay" : ""}`.replace(/\s+/g, " ").trim()}>
      {graphAvailable ? (
        <RepositoryConcepts
          concept={concept}
          contextActionListPath={landingRepository.contextActions?.listPath}
          contextDiagrams={landingRepository.contextDiagrams?.diagrams || []}
          contextOverview={landingRepository.contextOverview}
          contextPersonaListPath={landingRepository.contextPersonas?.listPath}
          contextStoryboards={landingRepository.contextStoryboards?.storyboards || []}
          issues={landingRepository.issues || []}
          motionPaused={!graphMotionStarted}
          onConceptChange={setConcept}
          onConceptClose={closeConcept}
          onConceptNavigate={selectConcept}
          onReplayClose={closeReplay}
          onReplayOpen={openReplay}
          onReplaySessionChange={handleReplaySessionChange}
          onSceneReady={() => {
            setGraphReady(true)
            setGraphMotionStarted(true)
          }}
          preloadReplay
          replayAutoplay
          replayPath={landingRepository.paths?.repositoryPlay}
          replayRequested={replayRequested}
          sectionActive
        />
      ) : (
        <div className="conceptive-landing-fallback-sky" aria-hidden="true">
          <span className="conceptive-landing-fallback-stars is-near" />
          <span className="conceptive-landing-fallback-stars is-far" />
        </div>
      )}

      {!graphAvailable ? (
        <p className="conceptive-landing-graph-status" id="conceptive-graph-status" role="status">
          Live graph temporarily unavailable
        </p>
      ) : null}

      <aside className="conceptive-landing-nav" aria-label="Conceptive home">
        <a href="/" className="conceptive-landing-brand" aria-label="Conceptive home">
          <ConceptiveConstellationMark className="conceptive-landing-logo" />
          <span>Conceptive</span>
        </a>

        <nav className="conceptive-landing-menu" aria-label="Learn about Conceptive">
          <a
            className="conceptive-landing-menu-item"
            href={playbackPath || "#conceptive-graph-status"}
            aria-describedby={graphAvailable ? undefined : "conceptive-graph-status"}
            onClick={openReplay}
          >
            <MenuSigil type="conceptive" />
            <span>What is Conceptive?</span>
          </a>
          <a className="conceptive-landing-get-started" href={paths.newGithubAuthorization}>
            <MenuSigil type="started" />
            <span>Get started</span>
          </a>
        </nav>

        <div className="conceptive-landing-command">
          <button type="button" onClick={copyInitCommand} aria-label="Copy conceptive init command" data-tooltip="Click to copy">
            <code><span aria-hidden="true">$</span> conceptive init</code>
            <span className="conceptive-landing-command-status" aria-live="polite">
              {commandCopied ? "Copied" : ""}
            </span>
          </button>
        </div>

        {developerControls ? (
          <div className="conceptive-landing-developer-controls">
            {developerControls}
          </div>
        ) : null}

        {alert ? <div className="conceptive-landing-alert" role="alert">{alert}</div> : null}
      </aside>
    </div>
  )
}
